import React, { useCallback, useState, useRef, useEffect, useMemo } from 'react'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import Input from 'core/elements/input'
import Button from 'core/elements/button'
import Calendar from 'react-calendar'
import { Popover } from '@material-ui/core'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import 'react-calendar/dist/Calendar.css'
import withFormContext, {
  PropsWithFormContext,
} from 'core/components/validatedForm/withFormContext'

type CalendarValue = Date | null | [Date | null, Date | null]

const useStyles = makeStyles<Theme>((theme) => ({
  calendarIconWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#0089C70D',
    border: `1px solid ${theme.palette.blue['700']}`,
    borderTopRightRadius: 4,
    borderBottomRightRadius: 4,
    padding: '0 12px',
    cursor: 'pointer',
    height: 36,
  },
  calendarIcon: {
    color: theme.palette.blue['700'],
    fontSize: 16,
  },
  inputWrapper: {
    cursor: 'pointer',
    maxWidth: 400,
    '& input': {
      cursor: 'pointer',
    },
  },
  popoverPaper: {
    borderRadius: 8,
    boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
    border: `1px solid ${theme.palette.grey[200]}`,
    overflow: 'hidden',
    zIndex: 1300,
  },
  pickerContainer: {
    padding: theme.spacing(2),
    // Calendar styling
    '& .react-calendar': {
      width: 280,
      border: 'none',
      fontFamily: theme.typography.fontFamily,
      backgroundColor: theme.palette.common.white,
    },
    // Navigation (month/year header)
    '& .react-calendar__navigation': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: theme.spacing(1),
      height: 36,
      '& button': {
        color: theme.palette.grey[700],
        fontSize: 14,
        fontWeight: 600,
        minWidth: 32,
        background: 'none',
        border: 'none',
        borderRadius: 4,
        fontFamily: theme.typography.fontFamily,
        '&:enabled:hover, &:enabled:focus': {
          backgroundColor: theme.palette.grey[100],
        },
        '&:disabled': {
          backgroundColor: 'transparent',
          color: theme.palette.grey[400],
        },
      },
    },
    '& .react-calendar__navigation__label': {
      fontWeight: 600,
      color: theme.palette.grey[700],
      fontFamily: theme.typography.fontFamily,
    },
    '& .react-calendar__navigation__arrow': {
      fontSize: 14,
      color: theme.palette.grey[700],
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 34,
      height: 34,
      '& i': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 34,
        height: 34,
      },
      '&:disabled': {
        cursor: 'not-allowed',
        color: theme.palette.grey[300],
        opacity: 0.5,
      },
    },
    '& .react-calendar__navigation__prev2-button, & .react-calendar__navigation__next2-button': {
      display: 'none',
    },
    // Weekday headers
    '& .react-calendar__month-view__weekdays': {
      textTransform: 'capitalize',
      fontWeight: 500,
      fontSize: 12,
      color: theme.palette.grey[500],
      fontFamily: theme.typography.fontFamily,
      '& abbr': {
        textDecoration: 'none',
      },
    },
    '& .react-calendar__month-view__weekdays__weekday': {
      padding: '8px 0',
    },
    // Day tiles
    '& .react-calendar__tile': {
      padding: '8px 4px',
      fontSize: 14,
      color: theme.palette.grey[700],
      borderRadius: 4,
      background: 'none',
      border: '1px solid transparent',
      transition: 'all 0.15s ease',
      fontFamily: theme.typography.fontFamily,
      aspectRatio: '1',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '&:enabled:hover, &:enabled:focus': {
        backgroundColor: theme.palette.grey[100],
      },
      '&:disabled': {
        color: theme.palette.grey[300],
      },
    },
    // Days from neighboring months
    '& .react-calendar__month-view__days__day--neighboringMonth': {
      color: theme.palette.grey[400],
    },
    // Today's date
    '& .react-calendar__tile--now': {
      backgroundColor: 'transparent',
      border: `1px solid ${theme.palette.grey[400]}`,
      '&:enabled:hover, &:enabled:focus': {
        backgroundColor: theme.palette.grey[100],
      },
    },
    // Selected date - border: primary blue 700, color: grayscale 700, bgColor: greyScale 100
    '& .react-calendar__tile--active': {
      backgroundColor: theme.palette.grey[100],
      color: `${theme.palette.grey[700]} !important`,
      fontWeight: 600,
      border: `1px solid ${theme.palette.primary.main}`,
      '&:enabled:hover, &:enabled:focus': {
        backgroundColor: theme.palette.grey[200],
      },
    },
    // Year/decade view tiles
    '& .react-calendar__year-view__months__month, & .react-calendar__decade-view__years__year, & .react-calendar__century-view__decades__decade': {
      padding: '24px 8px',
      borderRadius: 4,
      fontFamily: theme.typography.fontFamily,
      height: '0px !important',
    },
  },
  timeSection: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.grey[200]}`,
  },
  timeInputs: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  timeInputWrapper: {
    display: 'flex',
    alignItems: 'center',
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: 4,
    overflow: 'hidden',
  },
  timeInput: {
    width: 40,
    padding: '6px 8px',
    border: 'none',
    textAlign: 'center',
    fontSize: 14,
    color: theme.palette.grey[700],
    outline: 'none',
    '&::-webkit-inner-spin-button, &::-webkit-outer-spin-button': {
      opacity: 1,
    },
  },
  timeSeparator: {
    color: theme.palette.grey[600],
    fontWeight: 500,
    padding: '0 4px',
  },
  amPmSelect: {
    padding: '6px 8px',
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: 4,
    fontSize: 14,
    color: theme.palette.grey[700],
    cursor: 'pointer',
    backgroundColor: theme.palette.common.white,
    outline: 'none',
    '&:focus': {
      borderColor: theme.palette.primary.main,
    },
  },
  applyButtonWrapper: {
    marginTop: theme.spacing(2),
    '& button': {
      width: '100%',
    },
  },
}))

interface DateTimePickerFieldProps {
  id: string
  label?: string
  value?: string | Date | null
  onChange?: (value: string) => void
  required?: boolean
  info?: string
  placeholder?: string
  minDate?: Date
  maxDate?: Date
  disabled?: boolean
  className?: string
  /** Use date-only picker (no time selection) */
  dateOnly?: boolean
  /** Show Apply button - if false, onChange fires immediately on selection */
  showApplyButton?: boolean
}

const DateTimePickerField = ({
  id,
  label,
  value,
  onChange,
  required = false,
  info,
  errorMessage,
  placeholder = 'Select date',
  minDate,
  maxDate,
  disabled = false,
  className,
  dateOnly = false,
  showApplyButton = true,
}: PropsWithFormContext<string, DateTimePickerFieldProps>) => {
  const classes = useStyles()
  const anchorRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [calendarView, setCalendarView] = useState<'month' | 'year' | 'decade' | 'century'>('month')

  const parseValue = useCallback((val: string | Date | null | undefined): Date | null => {
    if (!val) return null
    if (val instanceof Date) return val
    const parsed = new Date(val)
    return isNaN(parsed.getTime()) ? null : parsed
  }, [])

  const initialDate = parseValue(value)
  const [selectedDate, setSelectedDate] = useState<Date | null>(initialDate)
  const [hours, setHours] = useState<number>(() => {
    if (!initialDate) return 7
    const h = initialDate.getHours()
    return h === 0 ? 12 : h > 12 ? h - 12 : h
  })
  const [minutes, setMinutes] = useState<number>(() => initialDate?.getMinutes() || 0)
  const [amPm, setAmPm] = useState<'AM' | 'PM'>(() => {
    if (!initialDate) return 'AM'
    return initialDate.getHours() >= 12 ? 'PM' : 'AM'
  })

  useEffect(() => {
    const parsed = parseValue(value)
    if (parsed) {
      setSelectedDate(parsed)
      const h = parsed.getHours()
      setHours(h === 0 ? 12 : h > 12 ? h - 12 : h)
      setMinutes(parsed.getMinutes())
      setAmPm(h >= 12 ? 'PM' : 'AM')
    } else {
      setSelectedDate(null)
      setHours(7)
      setMinutes(0)
      setAmPm('AM')
    }
  }, [value, parseValue])

  const handleCalendarChange = useCallback(
    (newValue: CalendarValue) => {
      if (newValue instanceof Date) {
        setSelectedDate(newValue)
        // For dateOnly mode or when Apply button is hidden, apply immediately
        if (dateOnly || !showApplyButton) {
          onChange(newValue.toISOString())
          setOpen(false)
        }
      }
    },
    [dateOnly, showApplyButton, onChange],
  )

  const handleApply = useCallback(() => {
    if (!selectedDate) return

    let hour24 = hours
    if (amPm === 'PM' && hours !== 12) {
      hour24 = hours + 12
    } else if (amPm === 'AM' && hours === 12) {
      hour24 = 0
    }

    const finalDate = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      selectedDate.getDate(),
      hour24,
      minutes,
      0,
    )

    // Validate that selected time is not in the past when minDate is set
    if (minDate && finalDate < new Date()) {
      return
    }

    onChange(finalDate.toISOString())
    setOpen(false)
  }, [selectedDate, hours, minutes, amPm, onChange, minDate])

  const formatDisplayValue = useCallback((): string => {
    const d = parseValue(value)
    if (!d) return ''

    const month = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    const year = d.getFullYear()

    if (dateOnly) {
      return `${month}/${day}/${year}`
    }

    let h = d.getHours()
    const m = String(d.getMinutes()).padStart(2, '0')
    const period = h >= 12 ? 'PM' : 'AM'
    h = h === 0 ? 12 : h > 12 ? h - 12 : h

    return `${month}/${day}/${year}, ${String(h).padStart(2, '0')}:${m} ${period}`
  }, [value, parseValue, dateOnly])

  const handleOpen = useCallback(() => {
    if (!disabled) {
      setOpen(true)
    }
  }, [disabled])

  const isTimeInPast = useMemo((): boolean => {
    if (!minDate || !selectedDate) return false

    const now = new Date()
    const isToday =
      selectedDate.getFullYear() === now.getFullYear() &&
      selectedDate.getMonth() === now.getMonth() &&
      selectedDate.getDate() === now.getDate()

    if (!isToday) return false

    let hour24 = hours
    if (amPm === 'PM' && hours !== 12) {
      hour24 = hours + 12
    } else if (amPm === 'AM' && hours === 12) {
      hour24 = 0
    }

    const selectedTime = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      selectedDate.getDate(),
      hour24,
      minutes,
      0,
    )

    return selectedTime < now
  }, [minDate, selectedDate, hours, minutes, amPm])

  return (
    <div className={className}>
      <div ref={anchorRef} onClick={handleOpen}>
        <Input
          id={id}
          label={label && required ? `${label} *` : label}
          value={formatDisplayValue()}
          placeholder={placeholder}
          readOnly
          disabled={disabled}
          error={errorMessage}
          info={info}
          className={classes.inputWrapper}
          endAdornment={
            <div className={classes.calendarIconWrapper}>
              <FontAwesomeIcon size="xl" className={classes.calendarIcon}>
                calendar-days
              </FontAwesomeIcon>
            </div>
          }
        />
      </div>

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          className: classes.popoverPaper,
        }}
        disableScrollLock
      >
        <div className={classes.pickerContainer}>
          <Calendar
            onChange={handleCalendarChange}
            value={selectedDate}
            minDate={minDate}
            maxDate={maxDate}
            locale="en-US"
            calendarType="iso8601"
            prevLabel={<FontAwesomeIcon>chevron-left</FontAwesomeIcon>}
            nextLabel={<FontAwesomeIcon>chevron-right</FontAwesomeIcon>}
            prev2Label={null}
            next2Label={null}
            onViewChange={({ view }) => setCalendarView(view)}
          />

          {!dateOnly && showApplyButton && calendarView === 'month' && (
            <>
              <div className={classes.timeSection}>
                <div className={classes.timeInputs}>
                  <div className={classes.timeInputWrapper}>
                    <input
                      type="number"
                      className={classes.timeInput}
                      value={hours}
                      onChange={(e) => {
                        const val = parseInt(e.target.value, 10)
                        if (val >= 1 && val <= 12) setHours(val)
                      }}
                      min={1}
                      max={12}
                    />
                  </div>
                  <span className={classes.timeSeparator}>:</span>
                  <div className={classes.timeInputWrapper}>
                    <input
                      type="number"
                      className={classes.timeInput}
                      value={String(minutes).padStart(2, '0')}
                      onChange={(e) => {
                        const val = parseInt(e.target.value, 10)
                        if (val >= 0 && val <= 59) setMinutes(val)
                      }}
                      min={0}
                      max={59}
                    />
                  </div>
                </div>
                <select
                  className={classes.amPmSelect}
                  value={amPm}
                  onChange={(e) => setAmPm(e.target.value as 'AM' | 'PM')}
                >
                  <option value="AM">AM</option>
                  <option value="PM">PM</option>
                </select>
              </div>

              <div className={classes.applyButtonWrapper}>
                <Button onClick={handleApply} disabled={isTimeInPast}>
                  Apply
                </Button>
              </div>
            </>
          )}
        </div>
      </Popover>
    </div>
  )
}

export default withFormContext(DateTimePickerField)
